import { gql } from '@apollo/client';
import type { TypedDocumentNode } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, LabelL } from '@sorare/core/src/atoms/typography';
import { Bold } from '@sorare/core/src/atoms/typography/Bold';
import { Dialog } from '@sorare/core/src/components/dialog';
import { Avatar } from '@sorare/core/src/components/user/Avatar';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';

import { RewardUnlockedStory } from '#src/components/missions/stories/RewardUnlockedStory/index.tsx';

import type { RefereeRewardDialog_currentUser } from './__generated__/index.graphql.ts';

type Props = {
  open: boolean;
  onClose: () => void;
  currentUser: Nullable<RefereeRewardDialog_currentUser>;
};

export const RefereeRewardDialog = ({ open, onClose, currentUser }: Props) => {
  const { inviteTaskAsReferee, referrer } = currentUser || {};
  if (!inviteTaskAsReferee) {
    return null;
  }
  return (
    <Dialog open={open} onClose={onClose} hideHeader>
      <RewardUnlockedStory
        onClose={onClose}
        task={inviteTaskAsReferee}
        showContinueButton={false}
        header={
          isType(referrer, 'User') ? (
            <Vertical center>
              <Avatar user={referrer} size={4} />
              <LabelL bold>{referrer?.nickname}</LabelL>
              <BodyS className="text-center">
                <FormattedMessage
                  defaultMessage="You earned rewards from <b>{referrer}</b> for finishing the onboarding"
                  values={{
                    b: Bold,
                    referrer: referrer?.nickname,
                  }}
                />
              </BodyS>
            </Vertical>
          ) : null
        }
        action={
          <Button color="primary" size="large" onClick={onClose}>
            <FormattedMessage {...glossary.continue} />
          </Button>
        }
      />
    </Dialog>
  );
};

RefereeRewardDialog.fragments = {
  currentUser: gql`
    fragment RefereeRewardDialog_currentUser on CurrentUser {
      slug
      inviteTaskAsReferee(sport: $sport) {
        id
        rewardConfigs {
          id
        }
        ...RewardUnlockedStory_taskInterface
      }
      referrer {
        slug
        nickname
        ...Avatar_user
      }
    }
    ${Avatar.fragments.user}
    ${RewardUnlockedStory.fragments.taskInterface}
  ` as TypedDocumentNode<RefereeRewardDialog_currentUser>,
};
